import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React from 'react';
import PropTypes from '../../../node_modules/prop-types/index.js';
import { DIRECTION } from '../constants.js';
import Errors from '../Errors/Errors.js';
import Legend from '../Legend/Legend.js';
import RadioButton from '../RadioButton/RadioButton.js';
import { safeRest, hasErrors, noop } from '../../utils/functions.js';
import handle from '../../utils/handle.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import { FlexBox, ItemGroup, Label, Note, Root, Box, Fieldset } from './styledComponents.js';
import useDerivedState from '../../hooks/useDerivedState.js';
import useId from '../../hooks/useId.js';
import useTheme from '../../hooks/useTheme.js';

var _excluded = ["id", "name", "legend", "values", "selected", "defaultSelected", "controlledSelected", "direction", "mobileDirection", "ariaRequired", "ariaLabel", "disabled", "errors", "errorCallout", "onChange", "customStyle"];

var RadioGroup = function RadioGroup(_ref) {
  var controlledId = _ref.id,
      controlledName = _ref.name,
      legend = _ref.legend,
      values = _ref.values,
      oldSelected = _ref.selected,
      defaultSelected = _ref.defaultSelected,
      controlledSelected = _ref.controlledSelected,
      direction = _ref.direction,
      mobileDirection = _ref.mobileDirection,
      ariaRequired = _ref.ariaRequired,
      ariaLabel = _ref.ariaLabel,
      disabled = _ref.disabled,
      errors = _ref.errors,
      errorCallout = _ref.errorCallout,
      onChange = _ref.onChange,
      customStyle = _ref.customStyle,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _useDerivedState = useDerivedState(useId('radio-id-'), controlledId),
      _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
      id = _useDerivedState2[0];

  var _useDerivedState3 = useDerivedState(useId('radio-name-'), controlledName),
      _useDerivedState4 = _slicedToArray(_useDerivedState3, 1),
      name = _useDerivedState4[0]; // oldSelected and defaultSelected are synonymous here until a major release when they will be
  // collapsed into one component. controlledSelected allows a consuming app to override the
  // default behaviour if necessary.


  var _useDerivedState5 = useDerivedState(oldSelected || defaultSelected, controlledSelected),
      _useDerivedState6 = _slicedToArray(_useDerivedState5, 2),
      selected = _useDerivedState6[0],
      setSelected = _useDerivedState6[1];

  var theme = useTheme();
  var safeProps = safeRest(rest);

  var _safeCustomStyle = safeCustomStyle(customStyle),
      labelStyle = _safeCustomStyle.labelStyle,
      noteStyle = _safeCustomStyle.noteStyle,
      rootStyle = _safeCustomStyle.rootStyle;

  var hasError = hasErrors(errors);
  var errorsId = hasError ? "".concat(id, "-errors") : undefined;
  var RadioContent = /*#__PURE__*/React.createElement(FlexBox, {
    "data-testid": "radio-group-test",
    className: "radio-group",
    direction: direction,
    mobileDirection: mobileDirection
  }, values.map(function (e) {
    var label = e.label,
        value = e.value,
        note = e.note;
    var isChecked = selected === value;
    var buttonId = "".concat(id, "-").concat(value);
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: value
    }, /*#__PURE__*/React.createElement(ItemGroup, {
      "data-testid": "item-group-".concat(value),
      key: value
    }, /*#__PURE__*/React.createElement(RadioButton, _extends({}, safeProps, {
      id: buttonId,
      name: name,
      type: "radio",
      value: value,
      checked: isChecked,
      "aria-describedby": errorsId,
      "aria-required": ariaRequired,
      "aria-label": label,
      onChange: disabled ? noop : function (checked, event) {
        setSelected(value);
        handle(onChange)(event, value);
      },
      disabled: disabled,
      customStyle: {
        radioStyle: customStyle.radioStyle
      },
      error: hasError,
      "data-testid": buttonId
    })), /*#__PURE__*/React.createElement(Label, {
      htmlFor: buttonId,
      labelStyle: labelStyle,
      checked: isChecked,
      disabled: disabled,
      theme: theme,
      "aria-hidden": true
    }, label)), direction === DIRECTION.COLUMN && note !== undefined && /*#__PURE__*/React.createElement(Note, {
      noteStyle: noteStyle
    }, note));
  }));
  return /*#__PURE__*/React.createElement(Root, {
    rootStyle: rootStyle
  }, /*#__PURE__*/React.createElement(Box, {
    "aria-label": ariaLabel
  }, /*#__PURE__*/React.createElement(Fieldset, {
    id: id,
    role: "radiogroup"
  }, /*#__PURE__*/React.createElement(Legend, {
    id: "".concat(id, "-legend"),
    customStyle: {
      legendStyle: customStyle.legendStyle
    }
  }, legend), RadioContent, /*#__PURE__*/React.createElement(Errors, {
    id: id,
    errors: errors,
    callouts: errorCallout
  }))));
};

process.env.NODE_ENV !== "production" ? RadioGroup.reduxFormProps = {
  inputProp: 'selected',
  inputType: 'string'
} : void 0;
process.env.NODE_ENV !== "production" ? RadioGroup.propTypes = {
  /** ID is required for Automated Testing, Google analytics and Google Tag Manager */
  id: PropTypes.string,

  /** Name is required for Automated Testing */
  name: PropTypes.string,

  /** Label to describe the Radio Button box */
  legend: PropTypes.string,

  /** Array of selectable labels/values (label can be JSX as well as a simple string) */
  values: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.node,
    value: PropTypes.string,
    note: PropTypes.string
  })),

  /**
   * The value of the radio button (from the values property) that is initially selected.
   * NOTE: Use defaultSelected instead. In a future release, this prop will instead
   * control the controlled behaviour of the component (see `controlledSelected`).
   * */
  selected: PropTypes.string,

  /**
   * Alias for `selected`. NOTE: This will be the standard prop for specifying initial/default
   * values once `selected` behaviour is updated.
   * */
  defaultSelected: PropTypes.string,

  /**
   * The value of the radio button (from the values property) that is currently selected.
   * NOTE: Currently the `selected` only specifies default behaviour, and can't actually control
   * the component. In a future release, the old `selected` prop's behaviour will change to mirror
   * this prop, and this prop will be deprecated.
   * */
  controlledSelected: PropTypes.string,

  /** Defines the direction of radio button group */
  direction: PropTypes.oneOf([DIRECTION.ROW, DIRECTION.COLUMN]),

  /** Defines the direction of radio button group for small screens */
  mobileDirection: PropTypes.oneOf([DIRECTION.ROW, DIRECTION.COLUMN]),

  /** Aria label for a11y */
  ariaLabel: PropTypes.string,

  /** Is this component required? (a11y) */
  ariaRequired: PropTypes.bool,

  /** Disables selection of textboxes */
  disabled: PropTypes.bool,

  /** List of errors */
  errors: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    // error id
    msg: PropTypes.any,
    // localized error text
    isError: PropTypes.bool // error condition

  })),

  /** Should the error messages be read out as an alert on a screen reader? */
  errorCallout: PropTypes.bool,

  /** Change handler for RadioButton */
  onChange: PropTypes.func,

  /** Style overrides */
  customStyle: PropTypes.shape({
    rootStyle: PropTypes.object,
    legendStyle: PropTypes.object,
    radioStyle: PropTypes.object,
    labelStyle: PropTypes.object,
    noteStyle: PropTypes.object
  })
} : void 0;
RadioGroup.defaultProps = {
  id: undefined,
  name: undefined,
  legend: '',
  values: [],
  selected: '',
  defaultSelected: '',
  controlledSelected: undefined,
  direction: DIRECTION.ROW,
  mobileDirection: DIRECTION.COLUMN,
  ariaLabel: '',
  ariaRequired: false,
  disabled: false,
  errors: [],
  errorCallout: false,
  onChange: noop,
  customStyle: {
    rootStyle: {},
    legendStyle: {},
    radioStyle: {},
    labelStyle: {},
    noteStyle: {}
  }
};
process.env.NODE_ENV !== "production" ? RadioGroup.displayName = 'RadioGroup' : void 0;
var RadioGroup$1 = RadioGroup;

export { RadioGroup$1 as default };
